'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { createClient } from '@/lib/supabase/client';

type ShipmentStatus =
  | 'created' | 'processing' | 'dispatched' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'failed_delivery' | 'returned' | 'cancelled';

interface Milestone {
  id: string;
  status: ShipmentStatus;
  location: string | null;
  description: string;
  occurred_at: string;
}

interface Shipment {
  id: string;
  tracking_number: string;
  description: string;
  origin: string;
  destination: string;
  recipient_name: string;
  recipient_phone: string | null;
  current_status: ShipmentStatus;
  carrier: string | null;
  weight_kg: number | null;
  declared_value: number | null;
  currency: string;
  estimated_delivery: string | null;
  notes: string | null;
  created_at: string;
  milestones?: Milestone[];
}

const STATUS_LABELS: Record<ShipmentStatus, string> = {
  created: 'Created',
  processing: 'Processing',
  dispatched: 'Dispatched',
  in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  failed_delivery: 'Failed Delivery',
  returned: 'Returned',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<ShipmentStatus, string> = {
  created: 'bg-slate-100 text-slate-600 border-slate-200',
  processing: 'bg-amber-50 text-amber-700 border-amber-200',
  dispatched: 'bg-blue-50 text-blue-700 border-blue-200',
  in_transit: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  out_for_delivery: 'bg-violet-50 text-violet-700 border-violet-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed_delivery: 'bg-red-50 text-red-700 border-red-200',
  returned: 'bg-orange-50 text-orange-700 border-orange-200',
  cancelled: 'bg-slate-100 text-slate-400 border-slate-200',
};

const PROGRESS_ORDER: ShipmentStatus[] = [
  'created', 'processing', 'dispatched', 'in_transit', 'out_for_delivery', 'delivered',
];

const MILESTONE_ICONS: Record<ShipmentStatus, React.ReactNode> = {
  created: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  ),
  processing: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  dispatched: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4" />
    </svg>
  ),
  in_transit: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10l2 1h8zM13 8h4l3 5v3h-7V8z" />
    </svg>
  ),
  out_for_delivery: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  delivered: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  ),
  failed_delivery: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  ),
  returned: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
    </svg>
  ),
  cancelled: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
};

function ProgressBar({ status }: { status: ShipmentStatus }) {
  const idx = PROGRESS_ORDER.indexOf(status);
  const isTerminal = ['failed_delivery', 'returned', 'cancelled'].includes(status);
  if (isTerminal) return null;
  const pct = idx < 0 ? 0 : Math.round(((idx + 1) / PROGRESS_ORDER.length) * 100);
  return (
    <div className="mt-4">
      <div className="flex justify-between text-[10px] text-slate-400 mb-1.5">
        <span>Created</span>
        <span>Delivered</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-slate-900 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between mt-1.5">
        {PROGRESS_ORDER.map((s, i) => (
          <div key={s} className="flex flex-col items-center gap-0.5">
            <div className={`w-2 h-2 rounded-full transition-colors ${i <= idx ? 'bg-slate-900' : 'bg-slate-200'}`} />
            <span className={`text-[9px] hidden sm:block ${i <= idx ? 'text-slate-600' : 'text-slate-300'}`}>
              {STATUS_LABELS[s].split(' ')[0]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PublicShipmentTrackingPage() {
  const supabase = createClient();
  const [inputValue, setInputValue] = useState('');
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim().toUpperCase();
    if (!trimmed) return;
    setLoading(true);
    setSearched(true);
    setError('');
    setShipment(null);

    try {
      const { data: shipmentData, error: shipmentError } = await supabase
        .from('shipments')
        .select('*')
        .eq('tracking_number', trimmed)
        .single();

      if (shipmentError || !shipmentData) {
        setError('No shipment found with that tracking number. Please check and try again.');
        setLoading(false);
        return;
      }

      const { data: milestones } = await supabase
        .from('shipment_milestones')
        .select('*')
        .eq('shipment_id', shipmentData.id)
        .order('occurred_at', { ascending: true });

      setShipment({ ...(shipmentData as Shipment), milestones: (milestones as Milestone[]) || [] });
    } catch {
      setError('An error occurred while fetching shipment details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb]">
      {/* Nav */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center">
              <Image
                src="/assets/images/ChatGPT_Image_Jul_15__2026__06_13_59_PM-1784139267730.png"
                alt="Kars Postasi Ltd logo"
                width={36}
                height={36}
                className="object-contain"
                style={{ mixBlendMode: 'multiply' }}
              />
            </div>
            <div>
              <p className="text-slate-900 font-bold text-[14px] leading-tight">KARS POSTASI LTD</p>
              <p className="text-slate-400 text-[9px] uppercase tracking-widest font-600">Security · Privacy · Trust</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link href="/track" className="text-[13px] font-600 text-slate-600 hover:text-slate-900 transition-colors px-3 py-2 rounded-lg hover:bg-slate-100">
              Storage Tracking
            </Link>
            <Link href="/sign-up-login-screen" className="text-[13px] font-600 text-slate-600 hover:text-slate-900 transition-colors px-3 py-2 rounded-lg hover:bg-slate-100">
              Sign In
            </Link>
            <Link href="/sign-up-login-screen" className="text-[13px] font-600 text-white bg-slate-900 px-4 py-2 rounded-lg hover:bg-slate-800 transition-all">
              Open Account
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-10">

        {/* Hero */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 text-[11px] font-600 text-slate-500 uppercase tracking-widest mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
            Public Shipment Tracker
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 leading-tight">Track Your Shipment</h1>
          <p className="text-[15px] text-slate-500 leading-relaxed">
            Enter your shipment tracking number to view real-time status and delivery milestones.
          </p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 max-w-2xl mx-auto">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder="Enter tracking number (e.g. KP-SHP-000123)"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 bg-slate-50 placeholder:text-slate-400"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !inputValue.trim()}
              className="px-6 py-3 bg-slate-900 text-white text-[14px] font-600 rounded-xl hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? 'Searching…' : 'Track Shipment'}
            </button>
          </form>
          <p className="text-[11px] text-slate-400 mt-3 text-center">
            Your tracking number can be found in your shipment confirmation email.
          </p>
        </div>

        {/* Results */}
        {searched && (
          <div>
            {loading ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 flex items-center justify-center gap-3">
                <svg className="animate-spin w-5 h-5 text-slate-400" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
                </svg>
                <span className="text-[14px] text-slate-500">Fetching shipment details…</span>
              </div>
            ) : error ? (
              <div className="bg-white rounded-2xl border border-red-100 p-8 text-center max-w-2xl mx-auto">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <p className="text-[14px] font-600 text-slate-700 mb-1">Shipment Not Found</p>
                <p className="text-[13px] text-slate-500">{error}</p>
              </div>
            ) : shipment ? (
              <div className="space-y-5">

                {/* Status card */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                    <div>
                      <p className="text-[11px] text-slate-400 uppercase tracking-widest font-600 mb-1">Tracking Number</p>
                      <p className="text-xl font-mono font-700 text-slate-900">{shipment.tracking_number}</p>
                      <p className="text-[13px] text-slate-500 mt-1">{shipment.description}</p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[12px] font-600 border ${STATUS_COLORS[shipment.current_status]}`}>
                      {STATUS_LABELS[shipment.current_status]}
                    </span>
                  </div>

                  <ProgressBar status={shipment.current_status} />

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide font-600 mb-1">From</p>
                      <p className="text-[13px] font-600 text-slate-800">{shipment.origin}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide font-600 mb-1">To</p>
                      <p className="text-[13px] font-600 text-slate-800">{shipment.destination}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide font-600 mb-1">Recipient</p>
                      <p className="text-[13px] font-600 text-slate-800">{shipment.recipient_name}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide font-600 mb-1">Est. Delivery</p>
                      <p className="text-[13px] font-600 text-slate-800">
                        {shipment.estimated_delivery ? formatDate(shipment.estimated_delivery) : '—'}
                      </p>
                    </div>
                  </div>

                  {(shipment.carrier || shipment.weight_kg) && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100">
                      {shipment.carrier && (
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase tracking-wide font-600 mb-1">Carrier</p>
                          <p className="text-[13px] font-600 text-slate-800">{shipment.carrier}</p>
                        </div>
                      )}
                      {shipment.weight_kg && (
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase tracking-wide font-600 mb-1">Weight</p>
                          <p className="text-[13px] font-600 text-slate-800">{shipment.weight_kg} kg</p>
                        </div>
                      )}
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wide font-600 mb-1">Created</p>
                        <p className="text-[13px] font-600 text-slate-800">{formatDate(shipment.created_at)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Milestones timeline */}
                {shipment.milestones && shipment.milestones.length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
                    <h2 className="text-[15px] font-700 text-slate-900 mb-6">Tracking Timeline</h2>
                    <div className="relative">
                      <div className="absolute left-5 top-0 bottom-0 w-px bg-slate-100" />
                      <div className="space-y-6">
                        {[...shipment.milestones].reverse().map((milestone, idx) => (
                          <div key={milestone.id} className="flex gap-4 relative">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2 ${idx === 0 ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-400 border-slate-200'}`}>
                              {MILESTONE_ICONS[milestone.status]}
                            </div>
                            <div className="flex-1 pb-2">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-600 border ${STATUS_COLORS[milestone.status]}`}>
                                  {STATUS_LABELS[milestone.status]}
                                </span>
                                <span className="text-[11px] text-slate-400">{formatDateTime(milestone.occurred_at)}</span>
                              </div>
                              <p className="text-[13px] text-slate-700">{milestone.description}</p>
                              {milestone.location && (
                                <p className="text-[12px] text-slate-400 mt-0.5 flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  {milestone.location}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* No milestones yet */}
                {(!shipment.milestones || shipment.milestones.length === 0) && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-[14px] font-600 text-slate-700 mb-1">No Updates Yet</p>
                    <p className="text-[13px] text-slate-500">Tracking milestones will appear here once your shipment is processed.</p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Footer note */}
        <div className="text-center pb-6">
          <p className="text-[12px] text-slate-400">
            Need help?{' '}
            <Link href="/contact" className="text-slate-600 hover:text-slate-900 underline underline-offset-2">Contact us</Link>
            {' '}or{' '}
            <Link href="/sign-up-login-screen" className="text-slate-600 hover:text-slate-900 underline underline-offset-2">sign in</Link>
            {' '}to view all your shipments.
          </p>
        </div>
      </div>
    </div>
  );
}
